import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    Users, UserPlus, Pencil, Trash2, Search, Calendar, X, Plus, Check,
    ClipboardList, UserCheck, Save, RefreshCw, Eye,
} from 'lucide-react';
import { useAuth, useCan } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const j = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${j}`;
};
const DATE_PRESETS = [
    { v: 'all', label: 'Tout' },
    { v: 'today', label: "Aujourd'hui" },
    { v: '7d', label: '7 jours' },
    { v: '30d', label: '30 jours' },
    { v: 'month', label: 'Ce mois' },
    { v: 'custom', label: 'Personnalisé' },
];

export default function Pointage() {
    const { user } = useAuth();
    const { canWrite: canWritePointage, canDelete: canDeletePointage } = useCan('espace_demenageurs');
    const isAdmin = user?.role === 'admin';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const validSubs = isAdmin ? ['affectation', 'demenageurs'] : ['affectation'];
    const initialSub = validSubs.includes(searchParams.get('sub')) ? searchParams.get('sub') : 'affectation';
    const [subTab, setSubTabState] = useState(initialSub);
    const setSubTab = (s) => {
        if (s === 'demenageurs' && !isAdmin) return; // protection
        setSubTabState(s);
        const sp = new URLSearchParams(searchParams);
        sp.set('tab', 'pointage');
        if (s === 'affectation') sp.delete('sub'); else sp.set('sub', s);
        setSearchParams(sp, { replace: false });
    };

    // --- Staff ---
    const [staff, setStaff] = useState([]);
    const [staffOpen, setStaffOpen] = useState(false);
    const [staffForm, setStaffForm] = useState({ id: null, nom: '', prenom: '', telephone: '', email: '', tarif_journee: '' });

    const fetchStaff = async () => {
        try {
            const r = await axios.get(`${API_URL}/api/demenageurs-staff`, { withCredentials: true });
            setStaff(r.data || []);
        } catch { /* noop */ }
    };

    const saveStaff = async () => {
        if (!staffForm.nom.trim()) { toast.error('Nom obligatoire'); return; }
        const payload = {
            nom: staffForm.nom,
            prenom: staffForm.prenom,
            telephone: staffForm.telephone,
            email: staffForm.email,
            tarif_journee: Number(staffForm.tarif_journee || 0),
        };
        try {
            if (staffForm.id) {
                await axios.put(`${API_URL}/api/demenageurs-staff/${staffForm.id}`, payload, { withCredentials: true });
                toast.success('Déménageur modifié');
            } else {
                await axios.post(`${API_URL}/api/demenageurs-staff`, payload, { withCredentials: true });
                toast.success('Déménageur ajouté');
            }
            setStaffForm({ id: null, nom: '', prenom: '', telephone: '', email: '', tarif_journee: '' });
            fetchStaff();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    const deleteStaff = async (id) => {
        const s = staff.find(x => x.id === id) || demView.find(x => x.id === id);
        const label = s ? `${s.prenom || ''} ${s.nom || ''}`.trim() : 'ce déménageur';
        if (!window.confirm(`Supprimer définitivement "${label}" ?\n\nCette action est irréversible. Les pointages existants resteront archivés mais ne seront plus visibles dans la liste.`)) return;
        try {
            await axios.delete(`${API_URL}/api/demenageurs-staff/${id}`, { withCredentials: true });
            toast.success(`${label} supprimé`);
            fetchStaff();
            fetchDemenageursView();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    // --- Events / affectation ---
    const [events, setEvents] = useState([]);
    const [affectOpen, setAffectOpen] = useState(false);
    const [affectRow, setAffectRow] = useState(null);
    const [affectForm, setAffectForm] = useState({ staff_ids: [], staff_details: {} });
    const [affectSearch, setAffectSearch] = useState('');

    const fetchEvents = async () => {
        try {
            const r = await axios.get(`${API_URL}/api/pointage/events`, { withCredentials: true });
            setEvents(r.data || []);
        } catch { /* noop */ }
    };

    const openAffect = (row) => {
        setAffectRow(row);
        setAffectSearch('');
        // Récupère staff_details existants, ou fallback depuis valeurs globales (ancien format)
        const incoming = row.staff_details || {};
        const sids = row.staff_ids || [];
        const details = {};
        sids.forEach(sid => {
            const d = incoming[sid];
            if (d) {
                details[sid] = {
                    nb_jours: d.nb_jours ?? 1,
                    panier: d.panier ?? '',
                    autre: d.autre ?? '',
                    remarque: d.remarque ?? '',
                };
            } else {
                // Legacy : distribue les globaux à chaque staff
                details[sid] = {
                    nb_jours: 1,
                    panier: row.panier || '',
                    autre: row.autre || '',
                    remarque: row.remarque || '',
                };
            }
        });
        setAffectForm({ staff_ids: sids, staff_details: details });
        setAffectOpen(true);
    };

    const toggleStaffInAffect = (sid) => {
        setAffectForm(p => {
            const isIn = p.staff_ids.includes(sid);
            if (isIn) {
                const { [sid]: _, ...rest } = p.staff_details;
                return { staff_ids: p.staff_ids.filter(x => x !== sid), staff_details: rest };
            }
            return {
                staff_ids: [...p.staff_ids, sid],
                staff_details: { ...p.staff_details, [sid]: { nb_jours: 1, panier: '', autre: '', remarque: '' } },
            };
        });
    };

    const updateStaffDetail = (sid, field, value) => {
        setAffectForm(p => ({
            ...p,
            staff_details: {
                ...p.staff_details,
                [sid]: { ...(p.staff_details[sid] || { nb_jours: 1, panier: '', autre: '', remarque: '' }), [field]: value },
            },
        }));
    };

    const saveAffect = async () => {
        try {
            // Normalise staff_details pour envoi
            const payloadDetails = {};
            affectForm.staff_ids.forEach(sid => {
                const d = affectForm.staff_details[sid] || {};
                payloadDetails[sid] = {
                    nb_jours: Number(d.nb_jours || 1) || 1,
                    panier: Number(d.panier || 0),
                    autre: Number(d.autre || 0),
                    remarque: d.remarque || '',
                };
            });
            await axios.post(`${API_URL}/api/pointage/affectation`, {
                event_id: affectRow.event_id,
                staff_ids: affectForm.staff_ids,
                staff_details: payloadDetails,
            }, { withCredentials: true });
            toast.success('Affectation enregistrée');
            setAffectOpen(false);
            fetchEvents();
            if (subTab === 'demenageurs') fetchDemenageursView();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    // --- Vue déménageurs ---
    const [demView, setDemView] = useState([]);
    const [viewLoading, setViewLoading] = useState(false);
    const [apercuOpen, setApercuOpen] = useState(false);
    const [apercuStaff, setApercuStaff] = useState(null);

    const fetchDemenageursView = async () => {
        setViewLoading(true);
        try {
            const r = await axios.get(`${API_URL}/api/pointage/demenageurs-view`, { withCredentials: true });
            setDemView(r.data || []);
        } catch { /* noop */ } finally {
            setViewLoading(false);
        }
    };

    useEffect(() => {
        fetchStaff();
        fetchEvents();
        fetchDemenageursView();
    }, []);

    // --- Filtres ---
    const [search, setSearch] = useState('');
    const [preset, setPreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const applyPreset = (p) => {
        setPreset(p);
        const today = new Date(); const iso = toISO(today);
        if (p === 'all') { setDateFrom(''); setDateTo(''); return; }
        if (p === 'today') { setDateFrom(iso); setDateTo(iso); return; }
        if (p === '7d') { const d = new Date(); d.setDate(d.getDate() - 6); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === '30d') { const d = new Date(); d.setDate(d.getDate() - 29); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === 'month') { const f = new Date(today.getFullYear(), today.getMonth(), 1); setDateFrom(toISO(f)); setDateTo(iso); return; }
    };
    const clearDates = () => { setPreset('all'); setDateFrom(''); setDateTo(''); };

    const matchDate = (d) => {
        if (!dateFrom && !dateTo) return true;
        if (!d) return false;
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
    };

    const eventsFiltered = events.filter(e => {
        if (search) {
            const s = search.toLowerCase();
            if (!(e.client_nom || '').toLowerCase().includes(s)) return false;
        }
        return matchDate(e.date);
    });

    const demViewFiltered = demView.map(s => ({
        ...s,
        journees: s.journees.filter(j => {
            if (search && !(j.client_nom || '').toLowerCase().includes(search.toLowerCase())) return false;
            return matchDate(j.date);
        }),
    })).filter(s => {
        if (search && !`${s.prenom} ${s.nom}`.toLowerCase().includes(search.toLowerCase()) && s.journees.length === 0) return false;
        return true;
    });

    const staffById = Object.fromEntries(staff.map(s => [s.id, s]));

    return (
        <div className="space-y-5" data-testid="pointage-wrapper">
            {/* Sous-onglets */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="pointage-subtabs">
                    <button
                        onClick={() => setSubTab('affectation')}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                            subTab === 'affectation' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-600 hover:text-emerald-600'
                        }`}
                        data-testid="ptg-tab-affectation"
                    >
                        <ClipboardList className="w-4 h-4" /> Affectation clients
                    </button>
                    {isAdmin && (
                        <button
                            onClick={() => setSubTab('demenageurs')}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                                subTab === 'demenageurs' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'
                            }`}
                            data-testid="ptg-tab-demenageurs"
                        >
                            <UserCheck className="w-4 h-4" /> Déménageurs
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <button
                            onClick={() => navigate('/mise-a-dispo')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 transition-colors"
                            data-testid="ptg-mise-a-dispo-btn"
                            title="Pointage des manutentionnaires mis à disposition d'autres entreprises"
                        >
                            <ClipboardList className="w-3.5 h-3.5" /> Mise à dispo
                        </button>
                    )}
                    {isAdmin && (
                        <button
                            onClick={() => setStaffOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B365D] text-white text-xs font-semibold hover:bg-[#152a4a] transition-colors"
                            data-testid="ptg-manage-staff"
                        >
                            <Users className="w-3.5 h-3.5" /> Gérer l'équipe ({staff.length})
                        </button>
                    )}
                    <button
                        onClick={() => { fetchEvents(); fetchDemenageursView(); }}
                        className="inline-flex items-center gap-1.5 text-sm text-[#1B365D] hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors"
                        data-testid="ptg-refresh"
                    >
                        <RefreshCw className="w-4 h-4" /> Rafraîchir
                    </button>
                </div>
            </div>

            {/* Filtres communs */}
            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        placeholder={subTab === 'affectation' ? 'Rechercher client...' : 'Rechercher déménageur ou client...'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="ptg-search"
                    />
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Calendar className="w-4 h-4 text-[#1B365D]" />
                    <span className="font-semibold text-[#1B365D] uppercase tracking-wide">Période</span>
                </div>
                <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
                    {DATE_PRESETS.map(p => (
                        <button key={p.v} onClick={() => applyPreset(p.v)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${preset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                            data-testid={`ptg-preset-${p.v}`}
                        >{p.label}</button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset('custom'); }} className="w-[160px] h-9 text-xs" data-testid="ptg-date-from" />
                    <span className="text-gray-400 text-xs">→</span>
                    <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset('custom'); }} className="w-[160px] h-9 text-xs" data-testid="ptg-date-to" />
                    {(dateFrom || dateTo) && (
                        <button onClick={clearDates} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                            <X className="w-3.5 h-3.5" /> Effacer
                        </button>
                    )}
                </div>
            </div>

            {/* ===== SOUS-ONGLET AFFECTATION ===== */}
            {subTab === 'affectation' && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr className="text-xs uppercase text-gray-500">
                                    <Th>Client</Th>
                                    <Th>Date</Th>
                                    <Th>Heure</Th>
                                    <Th>Type prestation</Th>
                                    <Th>Ville départ</Th>
                                    <Th center>Effectif</Th>
                                    <Th center>Panier</Th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {eventsFiltered.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center py-10 text-gray-400" data-testid="ptg-affect-empty">Aucun événement trouvé.</td></tr>
                                ) : eventsFiltered.map(e => (
                                    <tr key={e.event_id} className="hover:bg-gray-50" data-testid={`ptg-affect-row-${e.event_id}`}>
                                        <Td className="font-semibold text-[#1B365D]">{e.client_nom}</Td>
                                        <Td className="text-gray-700">{e.date}</Td>
                                        <Td className="text-gray-600">{e.heure_debut || '—'}{e.heure_fin ? ` → ${e.heure_fin}` : ''}</Td>
                                        <Td><Badge className="bg-gray-100 text-gray-700 border border-gray-200 text-[10px]">{e.type_prestation || '—'}</Badge></Td>
                                        <Td className="text-gray-600 max-w-[240px] truncate" title={e.ville_depart}>{e.ville_depart || '—'}</Td>
                                        <Td center>
                                            <button
                                                onClick={() => openAffect(e)}
                                                disabled={!canWritePointage}
                                                title={!canWritePointage ? 'Accès refusé : permission Écriture requise' : ''}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    (e.staff_ids || []).length > 0
                                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                                        : 'bg-[#1B365D] text-white hover:bg-[#152a4a]'
                                                }`}
                                                data-testid={`ptg-affect-btn-${e.event_id}`}
                                            >
                                                <Users className="w-3.5 h-3.5" />
                                                {(e.staff_ids || []).length > 0 ? `${e.staff_ids.length} déménageur${e.staff_ids.length > 1 ? 's' : ''}` : 'Affecter'}
                                            </button>
                                        </Td>
                                        <Td center className="text-gray-700">{e.panier ? `${Number(e.panier).toFixed(2)} €` : '—'}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ===== SOUS-ONGLET DÉMÉNAGEURS ===== */}
            {subTab === 'demenageurs' && isAdmin && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="ptg-demenageurs-view">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr className="text-xs uppercase text-gray-500">
                                    <Th>Nom déménageur</Th>
                                    <Th center>Total jours</Th>
                                    <Th right>Total panier</Th>
                                    <Th right>Total autre</Th>
                                    {isAdmin && <Th center>Actions</Th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {viewLoading ? (
                                    <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-10 text-gray-400">Chargement...</td></tr>
                                ) : demViewFiltered.length === 0 ? (
                                    <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-10 text-gray-400" data-testid="ptg-dem-empty">
                                        Aucun déménageur à afficher. Ajoutez d'abord une équipe via "Gérer l'équipe".
                                    </td></tr>
                                ) : demViewFiltered.map(s => {
                                    const totalPanier = s.journees.reduce((a, j) => a + Number(j.panier || 0), 0);
                                    const totalAutre = s.journees.reduce((a, j) => {
                                        const n = parseFloat(String(j.autre || '').replace(',', '.'));
                                        return a + (isNaN(n) ? 0 : n);
                                    }, 0);
                                    return (
                                        <tr key={s.id} className="hover:bg-gray-50" data-testid={`ptg-staff-row-${s.id}`}>
                                            <Td>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 rounded-full bg-[#1B365D] text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                        {(s.prenom?.[0] || '') + (s.nom?.[0] || '')}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold text-[#1B365D]">{s.prenom} {s.nom}</div>
                                                        {isAdmin && (
                                                            <div className="text-[11px] text-gray-500">Tarif : {Number(s.tarif_journee || 0).toFixed(2)} €/j</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Td>
                                            <Td center className="font-bold text-[#1B365D]">{s.journees.reduce((a, j) => a + Number(j.nb_jours || 1), 0)}</Td>
                                            <Td right className="font-bold text-emerald-600">{totalPanier.toFixed(2)} €</Td>
                                            <Td right className="font-medium text-gray-700">{totalAutre > 0 ? `${totalAutre.toFixed(2)} €` : '—'}</Td>
                                            {isAdmin && (
                                                <Td center>
                                                    <div className="inline-flex items-center gap-1">
                                                        <button
                                                            onClick={() => navigate(`/pointage/demenageur/${s.id}`)}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#1B365D] text-white text-xs font-medium hover:bg-[#152a4a] transition-colors"
                                                            title="Ouvrir la fiche déménageur"
                                                            data-testid={`ptg-staff-apercu-${s.id}`}
                                                        >
                                                            <Eye className="w-3.5 h-3.5" /> Aperçu
                                                        </button>
                                                        <button
                                                            onClick={() => deleteStaff(s.id)}
                                                            disabled={!canDeletePointage}
                                                            className="p-1.5 rounded-md text-red-500 hover:text-white hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-500"
                                                            title={!canDeletePointage ? 'Accès refusé : permission Suppression requise' : 'Supprimer ce déménageur'}
                                                            data-testid={`ptg-staff-delete-${s.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </Td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ===== DIALOG — Gestion équipe ===== */}
            <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
                <DialogContent className="max-w-lg" data-testid="ptg-staff-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <Users className="w-5 h-5" /> Gestion de l'équipe
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                        {/* Formulaire ajout/édition */}
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                                <Input placeholder="Nom *" value={staffForm.nom} onChange={e => setStaffForm(p => ({ ...p, nom: e.target.value }))} className="h-9 text-xs" data-testid="staff-nom" />
                                <Input placeholder="Prénom" value={staffForm.prenom} onChange={e => setStaffForm(p => ({ ...p, prenom: e.target.value }))} className="h-9 text-xs" data-testid="staff-prenom" />
                                {isAdmin ? (
                                    <Input type="number" step="0.01" placeholder="Tarif €/j" value={staffForm.tarif_journee} onChange={e => setStaffForm(p => ({ ...p, tarif_journee: e.target.value }))} className="h-9 text-xs" data-testid="staff-tarif" />
                                ) : (
                                    <div className="h-9 flex items-center px-3 rounded-md bg-gray-100 text-[10px] text-gray-400 italic select-none">Tarif (admin uniquement)</div>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <Input placeholder="Téléphone" value={staffForm.telephone} onChange={e => setStaffForm(p => ({ ...p, telephone: e.target.value }))} className="h-9 text-xs" data-testid="staff-tel" />
                                <Input type="email" placeholder="E-mail" value={staffForm.email} onChange={e => setStaffForm(p => ({ ...p, email: e.target.value }))} className="h-9 text-xs" data-testid="staff-email" />
                            </div>
                            <div className="flex gap-2 justify-end">
                                {staffForm.id && (
                                    <button onClick={() => setStaffForm({ id: null, nom: '', prenom: '', telephone: '', email: '', tarif_journee: '' })} className="px-3 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-200">
                                        Annuler
                                    </button>
                                )}
                                <button onClick={saveStaff}
                                    disabled={!canWritePointage}
                                    title={!canWritePointage ? 'Accès refusé : permission Écriture requise' : ''}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1B365D] text-white text-xs font-semibold hover:bg-[#152a4a] disabled:opacity-50 disabled:cursor-not-allowed" data-testid="staff-save">
                                    {staffForm.id ? <><Save className="w-3.5 h-3.5" /> Modifier</> : <><Plus className="w-3.5 h-3.5" /> Ajouter</>}
                                </button>
                            </div>
                        </div>

                        {/* Liste */}
                        <div className="max-h-[300px] overflow-y-auto space-y-1">
                            {staff.length === 0 ? (
                                <div className="text-center text-gray-400 text-sm py-6">Aucun déménageur pour le moment.</div>
                            ) : staff.map(s => (
                                <div key={s.id} className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-3 py-2 hover:bg-gray-50" data-testid={`staff-row-${s.id}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="h-8 w-8 rounded-full bg-[#1B365D] text-white flex items-center justify-center font-bold text-xs">
                                            {(s.prenom?.[0] || '') + (s.nom?.[0] || '')}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-[#1B365D] truncate">{s.prenom} {s.nom}</div>
                                            {isAdmin && (
                                                <div className="text-[11px] text-gray-500">{Number(s.tarif_journee || 0).toFixed(2)} €/j</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setStaffForm({ id: s.id, nom: s.nom, prenom: s.prenom, telephone: s.telephone || '', email: s.email || '', tarif_journee: s.tarif_journee })}
                                            className="p-1.5 rounded text-gray-500 hover:text-[#1B365D] hover:bg-gray-100"
                                            title="Modifier"
                                            data-testid={`staff-edit-${s.id}`}
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => deleteStaff(s.id)}
                                            disabled={!canDeletePointage}
                                            className="p-1.5 rounded text-red-500 hover:text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-red-500"
                                            title={!canDeletePointage ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                            data-testid={`staff-delete-${s.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setStaffOpen(false)} className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100">Fermer</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ===== DIALOG — Affectation ===== */}
            <Dialog open={affectOpen} onOpenChange={setAffectOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ptg-affect-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-emerald-600" /> Effectif & pointage
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                        {affectRow && (
                            <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                                <div className="font-semibold text-[#1B365D]">{affectRow.client_nom}</div>
                                <div className="text-xs text-gray-500">{affectRow.date} · {affectRow.heure_debut}{affectRow.heure_fin ? ` → ${affectRow.heure_fin}` : ''} · {affectRow.type_prestation}</div>
                            </div>
                        )}

                        {/* Barre de recherche */}
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                placeholder="Rechercher un déménageur..."
                                value={affectSearch}
                                onChange={e => setAffectSearch(e.target.value)}
                                className="pl-9 h-9 text-sm"
                                data-testid="affect-search"
                            />
                        </div>

                        {/* Liste déménageurs + données inline par sélection */}
                        <div className="flex items-center justify-between">
                            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                Déménageurs ({affectForm.staff_ids.length} sélectionné{affectForm.staff_ids.length > 1 ? 's' : ''})
                            </label>
                            {affectForm.staff_ids.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setAffectForm({ staff_ids: [], staff_details: {} })}
                                    className="text-[11px] text-gray-500 hover:text-red-600"
                                    data-testid="affect-clear"
                                >
                                    Tout décocher
                                </button>
                            )}
                        </div>
                        <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                            {staff.length === 0 ? (
                                <div className="p-4 text-center text-gray-400 text-sm">
                                    Aucun déménageur. <button onClick={() => { setAffectOpen(false); setStaffOpen(true); }} className="text-[#1B365D] underline">Ajouter</button>
                                </div>
                            ) : (() => {
                                const filtered = staff.filter(s => {
                                    if (!affectSearch) return true;
                                    const q = affectSearch.toLowerCase();
                                    return `${s.prenom || ''} ${s.nom || ''}`.toLowerCase().includes(q);
                                });
                                if (filtered.length === 0) {
                                    return <div className="p-4 text-center text-gray-400 text-xs">Aucun résultat.</div>;
                                }
                                return filtered.map(s => {
                                    const selected = affectForm.staff_ids.includes(s.id);
                                    const d = affectForm.staff_details[s.id] || { nb_jours: 1, panier: '', autre: '', remarque: '' };
                                    return (
                                        <div key={s.id} className={`${selected ? 'bg-emerald-50' : ''}`}>
                                            <button
                                                type="button"
                                                onClick={() => toggleStaffInAffect(s.id)}
                                                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                                                data-testid={`affect-staff-${s.id}`}
                                            >
                                                <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                                    {selected && <Check className="w-3 h-3 text-white" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-[#1B365D]">{s.prenom} {s.nom}</div>
                                                    {isAdmin && (
                                                        <div className="text-[10px] text-gray-500">{Number(s.tarif_journee || 0).toFixed(2)} €/j</div>
                                                    )}
                                                </div>
                                            </button>
                                            {selected && (
                                                <div className="px-3 pb-3 grid grid-cols-2 md:grid-cols-4 gap-2" onClick={e => e.stopPropagation()} data-testid={`affect-detail-${s.id}`}>
                                                    <InlineField label="Nb jours">
                                                        <Input
                                                            type="number"
                                                            step="0.5"
                                                            min="0"
                                                            value={d.nb_jours}
                                                            onChange={e => updateStaffDetail(s.id, 'nb_jours', e.target.value)}
                                                            className="h-8 text-xs"
                                                            data-testid={`affect-nbj-${s.id}`}
                                                        />
                                                    </InlineField>
                                                    <InlineField label="Panier (€)">
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="0.00"
                                                            value={d.panier}
                                                            onChange={e => updateStaffDetail(s.id, 'panier', e.target.value)}
                                                            className="h-8 text-xs"
                                                            data-testid={`affect-panier-${s.id}`}
                                                        />
                                                    </InlineField>
                                                    <InlineField label="Autre (€)">
                                                        <Input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="0.00"
                                                            value={d.autre}
                                                            onChange={e => updateStaffDetail(s.id, 'autre', e.target.value)}
                                                            className="h-8 text-xs"
                                                            data-testid={`affect-autre-${s.id}`}
                                                        />
                                                    </InlineField>
                                                    <InlineField label="Remarque">
                                                        <Input
                                                            placeholder="ex: société big"
                                                            value={d.remarque}
                                                            onChange={e => updateStaffDetail(s.id, 'remarque', e.target.value)}
                                                            className="h-8 text-xs"
                                                            data-testid={`affect-rem-${s.id}`}
                                                        />
                                                    </InlineField>
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setAffectOpen(false)} className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
                        <button onClick={saveAffect} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#152a4a]" data-testid="affect-save">
                            <Save className="w-4 h-4" /> Enregistrer
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ===== DIALOG — Aperçu détail déménageur ===== */}
            <Dialog open={apercuOpen} onOpenChange={setApercuOpen}>
                <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="ptg-apercu-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1B365D] flex items-center gap-3">
                            {apercuStaff && (
                                <div className="h-9 w-9 rounded-full bg-[#1B365D] text-white flex items-center justify-center font-bold text-xs">
                                    {(apercuStaff.prenom?.[0] || '') + (apercuStaff.nom?.[0] || '')}
                                </div>
                            )}
                            <div>
                                <div>{apercuStaff?.prenom} {apercuStaff?.nom}</div>
                                <div className="text-[11px] font-normal text-gray-500">
                                    {isAdmin && <>Tarif : {Number(apercuStaff?.tarif_journee || 0).toFixed(2)} €/j</>}
                                    {apercuStaff && <>{isAdmin ? ' · ' : ''}{apercuStaff.journees.length} journée{apercuStaff.journees.length > 1 ? 's' : ''}</>}
                                </div>
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-gray-200 bg-gray-50">
                                <tr className="text-[10px] uppercase text-gray-500">
                                    <Th>Date</Th>
                                    <Th>Client</Th>
                                    <Th>Ville départ</Th>
                                    <Th>Heure début</Th>
                                    <Th>Heure fin</Th>
                                    <Th>Type</Th>
                                    <Th right>Panier</Th>
                                    <Th>Autre</Th>
                                    <Th>Remarque</Th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {!apercuStaff || apercuStaff.journees.length === 0 ? (
                                    <tr><td colSpan={9} className="text-center py-8 text-gray-300 text-xs">Aucune journée.</td></tr>
                                ) : apercuStaff.journees.map(j => (
                                    <tr key={j.event_id} className="hover:bg-gray-50">
                                        <Td className="text-gray-700 font-medium">{j.date}</Td>
                                        <Td className="font-semibold text-[#1B365D]">{j.client_nom}</Td>
                                        <Td className="text-gray-600 max-w-[200px] truncate" title={j.ville_depart}>{j.ville_depart || '—'}</Td>
                                        <Td className="text-gray-700">{j.heure_debut || '—'}</Td>
                                        <Td className={`font-medium ${j.heure_fin ? 'text-emerald-600' : 'text-gray-300'}`}>{j.heure_fin || '—'}</Td>
                                        <Td><Badge className="bg-gray-100 text-gray-700 border border-gray-200 text-[10px]">{j.type_prestation || '—'}</Badge></Td>
                                        <Td right className="font-medium">{j.panier ? `${Number(j.panier).toFixed(2)} €` : '—'}</Td>
                                        <Td right className="font-medium">{j.autre ? `${Number(j.autre).toFixed(2)} €` : '—'}</Td>
                                        <Td className="text-gray-600 max-w-[180px] truncate" title={j.remarque}>{j.remarque || '—'}</Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setApercuOpen(false)} className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100" data-testid="ptg-apercu-close">Fermer</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Th({ children, center, right }) {
    return <th className={`px-3 py-3 text-left font-semibold ${center ? 'text-center' : ''} ${right ? 'text-right' : ''}`}>{children}</th>;
}
function Td({ children, center, right, className = '' }) {
    return <td className={`px-3 py-3 align-middle ${center ? 'text-center' : ''} ${right ? 'text-right' : ''} ${className}`}>{children}</td>;
}
function InlineField({ label, children }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</div>
            {children}
        </div>
    );
}
